import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import heroBg from '../../../assets/images/hero-bg.jpg';

const HeroSection = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(t);
  }, []);

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden bg-[#0A0A0F]">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src={heroBg}
          alt="background"
          className="w-full h-full object-cover object-top opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0F] via-[#0A0A0F]/80 to-[#0A0A0F]/40"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0F] via-transparent to-[#0A0A0F]/30"></div>
      </div>

      {/* Floating particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(12)].map((_, i) => (
          <div
            key={i}
            className="absolute rounded-full bg-[#C8A96E]/20 animate-pulse"
            style={{
              width: `${Math.random() * 4 + 2}px`,
              height: `${Math.random() * 4 + 2}px`,
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${Math.random() * 3 + 2}s`,
            }}
          />
        ))}
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 w-full pt-24 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <div className={`transition-all duration-1000 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="inline-flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-4 py-1.5 mb-8">
              <span className="w-1.5 h-1.5 rounded-full bg-[#C8A96E] animate-pulse"></span>
              <span className="text-white/60 text-xs uppercase tracking-widest">Plataforma de Louvor</span>
            </div>

            <h1 className="text-5xl md:text-6xl lg:text-7xl font-light text-white leading-[1.05] mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
              Música que<br />
              <em className="not-italic text-[#C8A96E]">une</em> e<br />
              <span className="font-bold">transforma.</span>
            </h1>

            <p className="text-white/50 text-lg leading-relaxed mb-10 max-w-md">
              Cifras inteligentes, transposição automática e colaboração em tempo real para músicos de louvor.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/auth?mode=register"
                className="inline-flex items-center justify-center gap-3 bg-[#C8A96E] text-[#0A0A0F] font-semibold px-8 py-4 rounded-full hover:bg-[#D4B87A] transition-all duration-300 whitespace-nowrap cursor-pointer group"
              >
                Começar gratuitamente
                <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
              </Link>
              <Link
                to="/library"
                className="inline-flex items-center justify-center gap-3 border border-white/20 text-white px-8 py-4 rounded-full hover:border-white/40 hover:bg-white/5 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-play-circle-line"></i>
                Ver demonstração
              </Link>
            </div>

            {/* Stats */}
            <div className="flex gap-10 mt-14 pt-10 border-t border-white/10">
              {[
                { value: '10K+', label: 'Músicos ativos' },
                { value: '50ms', label: 'Latência ao vivo' },
                { value: '∞', label: 'Tons disponíveis' },
              ].map((stat) => (
                <div key={stat.label}>
                  <p className="text-2xl font-bold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>{stat.value}</p>
                  <p className="text-white/40 text-xs mt-1">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Right — App Mockup */}
          <div className={`hidden lg:block transition-all duration-1000 delay-300 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="relative">
              {/* Main card */}
              <div className="bg-[#141414] border border-white/10 rounded-2xl overflow-hidden">
                {/* App header */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-white/5">
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#C8A96E]"></div>
                    <span className="text-white/80 text-sm font-medium">Oceans — Hillsong</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[#C8A96E] text-xs bg-[#C8A96E]/10 px-2 py-0.5 rounded-full">Tom: G</span>
                    <span className="text-white/30 text-xs">♩ = 72</span>
                  </div>
                </div>

                {/* Chord display */}
                <div className="p-6 font-mono text-sm">
                  <div className="mb-5">
                    <p className="text-white/30 text-xs mb-2 uppercase tracking-widest">Intro</p>
                    <div className="flex gap-6 flex-wrap">
                      {['G', 'D', 'Em', 'C'].map((chord) => (
                        <span key={chord} className="text-[#C8A96E] font-bold text-lg">{chord}</span>
                      ))}
                    </div>
                  </div>
                  <div className="mb-5">
                    <p className="text-white/30 text-xs mb-2 uppercase tracking-widest">Verso</p>
                    <p className="text-white/60 text-sm leading-loose">
                      <span className="text-[#C8A96E] font-bold">G</span>
                      {'  '}You call me out upon the{'  '}
                      <span className="text-[#C8A96E] font-bold">D</span>
                      {'  '}waters<br />
                      <span className="text-[#C8A96E] font-bold">Em</span>
                      {'  '}The great unknown where{'  '}
                      <span className="text-[#C8A96E] font-bold">C</span>
                      {'  '}feet may fail
                    </p>
                  </div>
                  <div>
                    <p className="text-white/30 text-xs mb-2 uppercase tracking-widest">Refrão</p>
                    <div className="flex gap-6 flex-wrap">
                      {['Em', 'C', 'G', 'D'].map((chord) => (
                        <span key={chord} className="text-[#C8A96E] font-bold text-lg">{chord}</span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Controls bar */}
                <div className="flex items-center justify-between px-5 py-4 bg-[#0F0F0F] border-t border-white/5">
                  <div className="flex gap-3">
                    {[
                      { icon: 'ri-skip-back-line', label: 'Anterior' },
                      { icon: 'ri-pause-circle-fill', label: 'Pausar', active: true },
                      { icon: 'ri-skip-forward-line', label: 'Próxima' },
                    ].map((btn) => (
                      <button
                        key={btn.label}
                        className={`w-8 h-8 flex items-center justify-center rounded-full cursor-pointer transition-colors ${btn.active ? 'text-[#C8A96E]' : 'text-white/40 hover:text-white/70'}`}
                      >
                        <i className={`${btn.icon} text-lg`}></i>
                      </button>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    {[
                      { icon: 'ri-arrow-up-line', label: 'Subir tom' },
                      { icon: 'ri-speed-line', label: 'Ralentar' },
                      { icon: 'ri-user-voice-line', label: 'Líder' },
                    ].map((btn) => (
                      <button
                        key={btn.label}
                        className="w-7 h-7 flex items-center justify-center text-white/30 hover:text-[#C8A96E] cursor-pointer transition-colors"
                        title={btn.label}
                      >
                        <i className={`${btn.icon} text-sm`}></i>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Floating badge — users online */}
              <div className="absolute -top-4 -right-4 bg-[#1A1915] border border-white/10 rounded-xl px-4 py-3 flex items-center gap-3">
                <div className="flex -space-x-2">
                  {['#C8A96E', '#7EB8A4', '#A97EC8'].map((color, i) => (
                    <div key={i} className="w-6 h-6 rounded-full border-2 border-[#1A1915]" style={{ backgroundColor: color }}></div>
                  ))}
                </div>
                <div>
                  <p className="text-white text-xs font-semibold">5 online</p>
                  <p className="text-white/30 text-xs">ao vivo agora</p>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
